import * as React from "react"
import * as ReactDOM from "react-dom"
import {ReportOwnProps, reducer as reportBuilderReducer, FilterWidgetValueParams, ReportTypeData, Axis} from "../lib/report-builder";
import {Action, applyMiddleware, createStore} from "redux";
import thunkMiddleware from "redux-thunk"
import {ReportState} from "../lib/report-builder";
import {CommandCaller} from "../lib/commands";
import {mapDispatchToProps, mapStateToProps} from "../lib/report-builder/lib";
import {DataModeDisplayer, StandardDisplayModes, standardModes} from "../lib/report-builder/graph-renderer";
import {connect, Store} from "react-redux";
import {ReportPropsCallback, ReportValueProps} from "../lib/report-builder/lib2";
import {Report} from "../lib/report-builder/lib1";
import {dateToYYYYMMDD, flatten, withDefault} from "../lib/utils";
import {FlatReportChartData} from "../lib/report-builder/chart-data";

type EMDisplayModes = StandardDisplayModes & {Statistics: DataModeDisplayer}

type EMAxes = {"email types": Axis}
type EMFilters = {date: FilterWidgetValueParams, messages: FilterWidgetValueParams}
type EMReports = {"Sent Messages": ReportTypeData<EMAxes, EMFilters, EMDisplayModes>}
type EMReportsOwnProps = ReportOwnProps<EMAxes, EMFilters, EMDisplayModes, EMReports, EMReportsState>
type EMReportsStoreOwnProps = ReportOwnProps<EMAxes, EMFilters, EMDisplayModes, EMReports, EMReportsState> &
	{
		store: Store<EMReportsState>,
	}

/**
 * todo: Consider flattening out the EMReportsState like so:
 * todo:
 * todo:     type EMReportsState = ReportState<EMAxes, EMFilters, EMReports, EMDisplayModes> & {stats: EMReportsStatsState}
 * todo:
 * todo: This would allow you to pass a EMReportsState into mapStateToProps in your ReportApp function
 * todo: (a instead of a.report), which should allow you to make use of the makeReportApp helper function from
 * todo: report manager.
 * todo:
 * todo: You could also then make use of other helpful types, for e.g. ReportStoreOwnProps rather than
 * todo: having to define your own
 */
type EMReportsState = {report: ReportState<EMAxes, EMFilters, EMDisplayModes, EMReports>, stats: EMReportsStatsState}
type EMReportsStatsState = undefined | {
		bouncedCount: string
		bouncedCountFormat: string
		invalidEmailCount: string
		invalidEmailCountFormat: string
		minDateReceivedFormat: string
		openedPercentage: string
		openedPercentageFormat: null
		openedTotal: string
		openedTotalFormat: string
		quantity: string
		quantityFormat: string
		sentCount: string
		sentCountFormat: string
		uniqueOpenedPercentage: string
		uniqueOpenedPercentageFormat: string
		uniqueOpenedTotal: string
		uniqueOpenedTotalFormat: string
		unsubscribedCount: string
		unsubscribedCountFormat: string
}

export type EMCallJsParams = {
	instance: string,
	messages: {'serial': string /* this is non-numeric. */, 'title': string}[]
	emailIdentifier: string[]
	ajaxPageUrl:string
	dates: {startYear: number, startMonth: number, startDay: number, endYear: number, endMonth: number, endDay: number}
}

const ReportApp =
	connect<
		ReportValueProps<EMAxes, EMFilters, EMDisplayModes, EMReports>,
		ReportPropsCallback<keyof EMReports, keyof EMFilters, EMDisplayModes>,
		EMReportsStoreOwnProps,
		EMReportsState
	>(
		(a, b) => mapStateToProps<EMAxes, EMFilters, EMDisplayModes, EMReports, EMReportsState>(a.report, b),
		(a, b) => mapDispatchToProps<EMAxes, EMFilters, EMDisplayModes, EMReports, EMReportsState>(a, b)
	)(Report)

type StatsAction = {type: "update report stats", stats: EMReportsStatsState}
const isStatsAction = (a: Action): a is StatsAction => a.type == "update report stats"

const statsReducer = (s: EMReportsStatsState, a: Action): EMReportsStatsState =>
	isStatsAction(a) ? a.stats : s

const reducer = (s: EMReportsState, a: Action): EMReportsState =>
	({
		report: reportBuilderReducer(s.report, a),
		stats: statsReducer(s.stats, a)
	})

export const addReportsToDiv = (div: HTMLDivElement, params: EMCallJsParams) => {
	const content = document.getElementById('content')
	if (content) {
		content.getElementsByTagName('table')[0].style.width = '100%';
	}

	const fromDate = new Date(params.dates.startYear, params.dates.startMonth, params.dates.startDay)
	const toDate = new Date(params.dates.endYear, params.dates.endMonth, params.dates.endDay)

	const initialState: EMReportsState = {
		report: {
			reportType: "Sent Messages",
			displayMode: "Graph",
			filters: {date: {type: "date range", fromDate, toDate}, messages: {type: "checkbox", checked: []}}
		},
		stats: undefined
	}

	const store = createStore<EMReportsState>(withDefault(reducer, initialState), initialState, applyMiddleware(thunkMiddleware))

	type CommandRunResultData = {DateFormat: string, bounced: number, opened: number, sent: number, unOpened: number}

	const isHidden = params.emailIdentifier.length > 0

	const props: EMReportsOwnProps = {
		modes: {...standardModes, Statistics: () => {
			const s = store.getState()
			const data = s.stats
				return !data ? <div>No data</div> : <table>
				<tr>
					<th>Query:</th>
					<td>{data.quantityFormat}</td>
				</tr>
				<tr>
					<th>Unsubscribed:</th>
					<td>{data.unsubscribedCountFormat}</td>
				</tr>
				<tr>
					<th>Invalid Email:</th>
					<td>{data.invalidEmailCountFormat}</td>
				</tr>
				<tr>
					<th>Bounced:</th>
					<td>{data.bouncedCountFormat}</td>
				</tr>
				<tr>
					<th>Sent:</th>
					<td>{data.sentCountFormat}</td>
				</tr>
				<tr>
					<th>Unique Opened:</th>
					<td>{data.uniqueOpenedTotalFormat}</td>
				</tr>
				<tr>
					<th>Unique Opened %:</th>
					<td>{data.uniqueOpenedPercentageFormat}</td>
				</tr>
			</table>}},
		reportTypes: {
			"Sent Messages": {
				label: "Sent Messages",
				axis: 'email types',
				filters: ["date", "messages"],
				renderAs: "line",
				removeEmptyAxes: true,
				keys: [{id: 'Date', label: 'Date'}],
				defaultDisplayMode: "Graph",
				displayModes: ["Graph", "Table", "Statistics"]
			}
		},
		filters: {date: {
			type: "date range", isHidden
			}, messages: {
			type: "checkbox", isHidden, label: "Messages", group: params.messages.map(m => ({value: m.serial, label: m.title}))}},
		axes: {"email types": [
				{label: "Undelivered Messages", value: "unOpened"},
				{value: "bounced", label: "Bounced Messages"},
				{value: "opened", label: "Opened Messages"},
				{value: "sent", label: "Sent Messages"}
		]},
		runReport: (dispatchWrapped) => async (dispatchUnwrapped, getState) => {
			const instance = params.instance
			const state = getState().report
			const date = state.filters.date
			const messages = state.filters.messages

			const identifiers = params.emailIdentifier.length == 1
				? [0, params.emailIdentifier[0]]  //some weird special case in the service
				: (messages.type == "checkbox"
					? messages.checked
					: params.emailIdentifier)

			const commandParams = {
				startDate: dateToYYYYMMDD(date.type == "date range" ? date.fromDate : new Date()),
				endDate: dateToYYYYMMDD(date.type == "date range" ? date.toDate : new Date()),
				emailIdentifiers: identifiers,
				instance
			}
			const a = await new CommandCaller(params.ajaxPageUrl).runGet('run sent emails report', commandParams) as {list: CommandRunResultData[], statistics: EMReportsStatsState}
			const action: StatsAction = {stats: a.statistics, type: "update report stats"}
			dispatchUnwrapped(action)
			dispatchWrapped({
				type: "set new report data",
				data: {
					renderAs: 'line',
					axis: "email types",
					data: flatten(a.list.map((item): FlatReportChartData => [
						{id: 'unOpened', key: item.DateFormat, value: item.unOpened},
						{id: 'opened', key: item.DateFormat, value: item.opened},
						{id: 'sent', key: item.DateFormat, value: item.sent},
						{id: 'bounced', key: item.DateFormat, value: item.bounced},
					]))
				}
			})
		},
		title: "Email Reports"
	}
	ReactDOM.render(<ReportApp {...props} store={store} />, div)
	const dispatchers = mapDispatchToProps(store.dispatch, props)
	dispatchers.runReport()
}



// WEBPACK FOOTER //
// ./src/emailmanager/reports.tsx